/**
 * Size Guide modal — vanilla JS, no dependencies.
 *
 * Trigger:   <button data-size-guide-trigger="size-guide-modal-{id}">
 * Dialog:    <dialog id="size-guide-modal-{id}" class="size-guide-modal dialog-modal">
 * Close btn: <button data-size-guide-close>
 * Unit toggle: <button data-size-guide-unit="in|cm">
 *
 * Close behaviours:
 *   • Close button click
 *   • Backdrop click  (clicking the <dialog> element outside its padding box)
 *   • Escape key      (intercepted to play the exit animation first)
 */
(function () {
  'use strict';

  /**
   * Closes a size-guide dialog with the theme's dialog-closing exit animation.
   * Falls back to an immediate close if no animation is running (e.g. reduced-motion).
   *
   * @param {HTMLDialogElement} dialog
   */
  function closeSizeGuide(dialog) {
    if (!dialog || !dialog.open) return;

    dialog.classList.add('dialog-closing');

    // Check whether an animation is actually playing before waiting for it.
    var style = window.getComputedStyle(dialog);
    var animDuration = parseFloat(style.animationDuration) || 0;

    if (animDuration > 0) {
      dialog.addEventListener('animationend', function onEnd() {
        dialog.removeEventListener('animationend', onEnd);
        dialog.close();
        dialog.classList.remove('dialog-closing');
      }, { once: true });
    } else {
      // No animation — close immediately.
      dialog.close();
      dialog.classList.remove('dialog-closing');
    }
  }

  /**
   * Opens a size-guide dialog.
   *
   * @param {HTMLDialogElement} dialog
   */
  function openSizeGuide(dialog) {
    if (!dialog || dialog.open) return;
    dialog.showModal();
  }

  // ── Escape key: intercept before the browser closes the dialog natively ──
  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Escape') return;
      var dialog = document.querySelector('.size-guide-modal[open]');
      if (!dialog) return;
      // Prevent the browser's built-in close so our animation can play.
      event.preventDefault();
      closeSizeGuide(dialog);
    },
    true // capture phase so we fire before the dialog's own handler
  );

  // ── Click delegation ──────────────────────────────────────────────────────
  document.addEventListener('click', function (event) {
    // 1. Open trigger
    var trigger = event.target.closest('[data-size-guide-trigger]');
    if (trigger) {
      var dialogId = trigger.getAttribute('data-size-guide-trigger');
      var dialog = document.getElementById(dialogId);
      openSizeGuide(dialog);
      return;
    }

    // 2. Explicit close button
    var closeBtn = event.target.closest('[data-size-guide-close]');
    if (closeBtn) {
      var closeDialog = closeBtn.closest('dialog.size-guide-modal');
      if (closeDialog) closeSizeGuide(closeDialog);
      return;
    }

    // 3. IN / CM unit toggle
    var unitBtn = event.target.closest('[data-size-guide-unit]');
    if (unitBtn) {
      var modal = unitBtn.closest('.size-guide-modal');
      if (!modal) return;
      var isCm = unitBtn.getAttribute('data-size-guide-unit') === 'cm';
      modal.classList.toggle('is-cm', isCm);
      modal.querySelectorAll('[data-size-guide-unit]').forEach(function (btn) {
        btn.setAttribute('aria-pressed', String(btn.getAttribute('data-size-guide-unit') === (isCm ? 'cm' : 'in')));
      });
      return;
    }

    // 3. Backdrop click — the <dialog> element IS the event target when the
    //    user clicks the ::backdrop or the dialog's own padding area.
    if (
      event.target instanceof HTMLDialogElement &&
      event.target.classList.contains('size-guide-modal')
    ) {
      // Confirm the click landed outside the rendered content box.
      var rect = event.target.getBoundingClientRect();
      var outside =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;

      if (outside) closeSizeGuide(event.target);
    }
  });
})();
